import { type ReactiveController, type ReactiveElement } from "lit";

/**
 * Reactive controller for observing element resize events with automatic state management.
 *
 * Features:
 * - Reactive `result` property containing the latest DOMRectReadOnly
 * - Lazy element resolution using getter function
 * - Automatic lifecycle management
 * - Seamless integration with Lit's reactive system
 * - Render-cycle-synced updates to prevent race conditions and optimize performance
 * - Natural throttling of rapid resize events via requestAnimationFrame
 *
 * @example
 * ```typescript
 * class MyComponent extends LitElement {
 *   private canvasSize = new ResizeObserverController(this, () => this.canvas);
 *   private sliderSize = new ResizeObserverController(this, () => this.sliderContainer);
 *
 *   render() {
 *     const width = this.canvasSize.result?.width || 0;
 *     return html`<div>Canvas width: ${width}px</div>`;
 *   }
 * }
 * ```
 */
export class ResizeObserverController implements ReactiveController {
  /**
   * Static debouncing mechanism to prevent race conditions when multiple
   * ResizeObserver controllers exist on the same component.
   *
   * Uses WeakSet for automatic memory cleanup when host elements are garbage collected.
   */
  private static pendingUpdates = new WeakSet<ReactiveElement>();

  private resizeObserver?: ResizeObserver;
  private currentTarget?: Element;
  private _result?: DOMRectReadOnly;

  /**
   * Creates a new ResizeObserver controller
   *
   * @param host - The Lit element that owns this controller
   * @param elementGetter - Function that returns the element to observe (allows lazy evaluation)
   */
  constructor(
    private host: ReactiveElement,
    private elementGetter: () => Element | null | undefined,
  ) {
    this.host.addController(this);
  }

  /**
   * The current size information of the observed element
   * Returns undefined if no element is being observed or no resize has occurred yet
   */
  get result(): DOMRectReadOnly | undefined {
    return this._result;
  }

  hostUpdated(): void {
    // Only re-evaluate if element reference actually changed
    const element = this.elementGetter();
    if (this.currentTarget !== element) {
      this.startObserving();
    }
  }

  hostDisconnected(): void {
    this.cleanup();
  }

  /**
   * Requests a host update with debouncing to prevent race conditions
   * when multiple ResizeObserver controllers exist on the same component.
   *
   * Uses requestAnimationFrame to sync updates with the browser's rendering cycle,
   * ensuring updates happen after all layout changes are complete and providing
   * natural performance optimization by batching rapid resize events.
   */
  private requestHostUpdate(): void {
    // Only schedule update if not already pending for this host
    if (!ResizeObserverController.pendingUpdates.has(this.host)) {
      ResizeObserverController.pendingUpdates.add(this.host);

      // Use requestAnimationFrame to sync with browser's rendering cycle
      requestAnimationFrame(() => {
        ResizeObserverController.pendingUpdates.delete(this.host);
        this.host.requestUpdate();
      });
    }
  }

  /**
   * Starts observing the element returned by the element getter
   * If the element changes, automatically switches to observe the new element
   */
  private startObserving(): void {
    const element = this.elementGetter();

    // If element is the same as current target, no change needed
    if (this.currentTarget === element) return;

    // Clean up previous observer
    this.cleanup();

    // Start observing new element if it exists
    if (element) {
      this.currentTarget = element;

      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          this._result = entry.contentRect;
          this.requestHostUpdate();
        }
      });

      this.resizeObserver.observe(element);

      // Get initial size immediately without triggering update
      // This prevents infinite loop in hostUpdated()
      this._result = element.getBoundingClientRect();
    }
  }

  /**
   * Stops observing and cleans up resources
   */
  private cleanup(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    this.currentTarget = undefined;
    this._result = undefined;
  }
}
